import { useQuery } from "@tanstack/react-query";
import { Button, Form, Input, message, Table, Tag } from "antd";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { getMembers, sendInvite } from "../actions/acc_actions";
import ContentLayout from "../components/layout/ContentLayout";
import MyBreadcrumb from "../components/layout/MyBreadcrumb";
import Loading from "../utils/Loading";

export default function SettingsRoles() {
  const { t } = useTranslation();
  const accUrn = useSelector((state) => state.account.urn);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  /* ------------------------------
     Table state (CONTROLLED)
  ------------------------------ */
  const [tableState, setTableState] = useState({
    current: 1,
    pageSize: 10,
  });

  const [search, setSearch] = useState("");

  /* ------------------------------
     Fetch members
  ------------------------------ */
  const { refetch, isLoading, data } = useQuery({
    queryKey: ["members", accUrn],
    queryFn: () => getMembers(accUrn),
    enabled: !!accUrn,
  });

  /* ------------------------------
     Normalize users + invites
  ------------------------------ */
  const tableData = useMemo(() => {
    if (!data) return [];

    const users =
      data.users?.map((u) => ({
        key: u.urn,
        type: "user",
        firstName: u.first_name || "-",
        lastName: u.last_name || "-",
        email: u.email_address || "-",
        roles: u.roles?.join(", "),
        created: null,
      })) || [];

    const invites =
      data.invites?.map((i) => ({
        key: i.urn,
        type: "invite",
        firstName: i.first_name,
        lastName: i.last_name,
        email: i.email_address,
        roles: t("pending"),
        created: i.created,
      })) || [];

    return [...users, ...invites];
  }, [data, t]);

  /* ------------------------------
     Local search (email)
  ------------------------------ */
  const filteredData = useMemo(() => {
    if (!search) return tableData;

    return tableData.filter((row) =>
      row.email.toLowerCase().includes(search.toLowerCase()),
    );
  }, [tableData, search]);

  /* ------------------------------
     Table columns
  ------------------------------ */
  const columns = [
    {
      title: t("type"),
      dataIndex: "type",
      width: 100,
      render: (type) =>
        type === "user" ? (
          <Tag color="green">{t("user")}</Tag>
        ) : (
          <Tag color="orange">{t("invite")}</Tag>
        ),
    },
    {
      title: t("first_name"),
      dataIndex: "firstName",
      width: 160,
      responsive: ["md"],
    },
    {
      title: t("last_name"),
      dataIndex: "lastName",
      width: 160,
      responsive: ["md"],
    },
    {
      title: t("email_address"),
      dataIndex: "email",
      width: 280,
      ellipsis: true,
      sorter: (a, b) => a.email.localeCompare(b.email),
    },
    {
      title: t("roles"),
      dataIndex: "roles",
      width: 180,
      responsive: ["lg"],
    },
    {
      title: t("created_at"),
      dataIndex: "created",
      width: 140,
      responsive: ["lg"],
      sorter: (a, b) => new Date(a.created || 0) - new Date(b.created || 0),
      render: (date) => (date ? dayjs(date).format("YYYY-MM-DD") : "-"),
    },
  ];

  /* ------------------------------
     Table change handler
  ------------------------------ */
  const handleTableChange = (pagination) => {
    setTableState({
      current: pagination.current,
      pageSize: pagination.pageSize,
    });
  };

  /* ------------------------------
     Invite submit
  ------------------------------ */
  const onFinish = (values) => {
    const email = values.email.toLowerCase();

    const userExists = data?.users?.some(
      (u) => u.email_address?.toLowerCase() === email,
    );
    const inviteExists = data?.invites?.some(
      (i) => i.email_address?.toLowerCase() === email,
    );

    // if (userExists) return message.error(t("email_already_exists"));
    // if (inviteExists) return message.error(t("invite_pending"));

    const payload = {
      ...values,
      roles: ["admin"],
      accountUrn: accUrn,
    };

    setLoading(true);
    sendInvite(payload)
      .then(() => {
        message.success(t("invite_sent_success"));
        refetch();
        form.resetFields();
      })
      .catch(() => message.error(t("something_went_wrong")))
      .finally(() => setLoading(false));
  };

  if (isLoading) return <Loading />;

  return (
    <ContentLayout
      header={
        <>
          <h1>{t("account_roles")}</h1>
          <MyBreadcrumb />
        </>
      }
    >
      {/* ================= TABLE ================= */}
      <h5 className="active" style={{ marginBottom: "1rem" }}>
        {t("users")}
      </h5>

      <Input
        placeholder={t("search_by_email")}
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setTableState((s) => ({ ...s, current: 1 }));
        }}
        style={{ maxWidth: 320, marginBottom: 16 }}
        allowClear
      />

      <Table
        columns={columns}
        dataSource={filteredData}
        rowKey="key"
        pagination={{
          current: tableState.current,
          pageSize: tableState.pageSize,
          total: filteredData.length,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
        }}
        onChange={handleTableChange}
        scroll={{ x: 1000 }}
        size="middle"
      />

      {/* ================= FORM ================= */}
      <Form
        form={form}
        layout="vertical"
        className="rewards-form"
        onFinish={onFinish}
      >
        <div className="rewards-form-section roles-form-section">
          <h5 className="active">{t("invite_team_members")}</h5>

          <div className="rewards-inputs">
            <Form.Item
              name="email"
              label={t("email_address")}
              rules={[
                { required: true, message: t("please_enter_email") },
                { type: "email", message: t("please_enter_valid_email") },
              ]}
            >
              <Input placeholder={t("enter_email_address")} />
            </Form.Item>

            <div className="form-row">
              <Form.Item
                name="firstName"
                label={t("first_name")}
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="lastName"
                label={t("last_name")}
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </div>
          </div>
        </div>

        <Form.Item className="rewards-form-btn">
          <Button loading={loading} type="primary" htmlType="submit">
            {loading ? t("sending") : t("send_invite")}
          </Button>
        </Form.Item>
      </Form>
    </ContentLayout>
  );
}
